//Global imports
import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormInput,
    CFormLabel,
    CFormTextarea,
} from '@coreui/react';
import React, { useEffect, useRef, useState } from 'react';
import JoditEditor from 'jodit-react';
import { useLocation, useNavigate } from 'react-router-dom';

//File imports
import { APP_LANGAUGE, LANGAUGE_NAME } from '../../Constant';
import { Language } from '../../language/English';
import Notification from '../../services/Notification/Notification';
import { ArticleTranslationService } from './Api/ArticleTranslationService';
import Logger from '../../services/Logger/Logger';
import { ArticleTranslationPojo } from './Api/ArticleTranslationPojo';

//Component imports
import Loader from '../../components/Layout/Loader';
import { Layout } from '../../components/Layout';
import { HintText, LengthChecker } from '../../components';
import CIcon from '@coreui/icons-react';
import { cilX } from '@coreui/icons';
import { API_EDITOR_IMAGE_UPLOAD } from './Api/ArticleTranslationConstants';

interface FormErrors {
    articleTitle?: string;
    url?: string;
    metaTitle?: string;
    metaKeyword?: string;
    metaDescription?: string;
    youtubeLink?: string;
    shortDescription?: string;
    description?: string;
    thumbnail?: string;
}

const ArticleTranslation = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const editor = useRef(null);
    const location = useLocation();
    const fileInputRef = useRef<null | HTMLInputElement>(null);
    const { id, language, isEdit, name, isFooterLink } = location.state;
    const [Editorconfig, setEditorconfig] = useState({
        height: 350,
    });
    const notification = new Notification();
    const articleService = new ArticleTranslationService();
    const logger = new Logger();
    const navigate = useNavigate();

    const [formData, setFormData] = useState<any>({
        articleTitle: '',
        url: '',
        metaTitle: '',
        metaKeyword: '',
        metaDescription: '',
        youtubeLink: '',
        documentText: '',
        shortDescription: '',
        description: '',
        thumbnail: null,
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [translationDataLoading, setTranslationDataLoading] =
        useState<boolean>(false);
    const [translationData, setTranslationData] =
        useState<ArticleTranslationPojo | null>(null);
    const [folderName, setFolderName] = useState<string | null>(null);
    const [imageChanged, setImageChanged] = useState<boolean>(false);

    useEffect(() => {
        if (isEdit) {
            setTranslationDataLoading(true);
            getArticleTranslationData();
        }
    }, [isEdit]);

    useEffect(() => {
        const JoditEditorConfig = {
            height: 1000,
            readonly: false,
            askBeforePasteHTML: false,
            addNewLineOnDBLClick: false,
            enableDragAndDropFileToEditor: true,
            uploader: {
                url: API_EDITOR_IMAGE_UPLOAD,
                insertImageAsBase64URI: false,
                imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'webp'],
                headers: { Authorization: `Bearer ${user.token}` },
                filesVariableName: function (t: string) {
                    return 'files[' + t + ']';
                },
                withCredentials: false,
                pathVariableName: 'path',
                format: 'json',
                method: 'POST',
                prepareData: function (formData: any) {
                    setShowLoader(true);
                    formData.append('folderName', folderName);
                    return formData;
                },
                isSuccess: function (e: any) {
                    return e && e.response;
                },
                process: function (resp: any) {
                    return {
                        files: resp.response,
                    };
                },
                error: function (this: any, e: Error) {
                    this.j.e.fire('errorMessage', e.message, 'error', 4000);
                    setShowLoader(false);
                },
                defaultHandlerSuccess: function (this: any, resp: any) {
                    const j = this;
                    if (resp.files && resp.files.length) {
                        const tagName = 'img';
                        resp.files.forEach((data: any) => {
                            setFolderName(data.uniqueFolderName);
                            const elm = j.createInside.element(tagName);
                            elm.setAttribute('src', data.filePath);
                            j.s.insertImage(
                                elm as HTMLImageElement,
                                null,
                                j.o.imageDefaultWidth,
                            );
                        });
                    }
                    setShowLoader(false);
                },
                defaultHandlerError: function (this: any, e: { message: any }) {
                    this.j.e.fire('errorMessage', e.message);
                    setShowLoader(false);
                },
            },
        };

        setEditorconfig(JoditEditorConfig);
    }, [folderName, user.token]);

    const getArticleTranslationData = async () => {
        try {
            let payload = {
                articleId: id,
                languageId: APP_LANGAUGE[language],
            };

            const { success, message, data } =
                await articleService.get(payload);
            const {
                title,
                url,
                metaTitle,
                metaKeyword,
                metaDescription,
                youtubeVideoLink,
                shortDescription,
                description,
                thumbnail,
                folderName,
            }: ArticleTranslationPojo = data;
            if (success) {
                setTranslationData(data);
                setFolderName(folderName);
                setFormData({
                    articleTitle: title,
                    url,
                    metaTitle,
                    metaKeyword,
                    metaDescription,
                    youtubeLink: youtubeVideoLink ?? '',
                    shortDescription,
                    description,
                    thumbnail,
                });
            } else {
                notification.error(
                    message ?? Language.UNABLE_TO_FETCH_TRANSLATION_DATA,
                );
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setTranslationDataLoading(false);
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === convertToCamelCase(Language.URL)) {
            newValue = newValue.toLowerCase();
        }
        setFormData((prev: any) => ({ ...prev, [name]: newValue }));

        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        const regexForCheckingCommaSeperatedString =
            /^([A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)(,\s*[A-Za-zÀ-ÖØ-öø-ÿ0-9!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?`~\s]+?)*$/;
        const regexForCheckingUrl = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9-]+$/;
        const youtubeRegex =
            /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?$/;

        let isValid = true;

        if (!formData.thumbnail) {
            newErrors.thumbnail = Language.THUMBNAIL_REQUIRED;
            isValid = false;
        }

        if (!formData.articleTitle.trim()) {
            newErrors.articleTitle = Language.ARTICLE_TITLE_REQUIRED;
            isValid = false;
        }

        if (!formData.url.trim()) {
            newErrors.url = Language.URL_REQUIRED;
            isValid = false;
        } else if (!regexForCheckingUrl.test(formData.url)) {
            newErrors.url = Language.URL_SHOULD_NOT_CONTAIN_WHITE_SPACE;
            isValid = false;
        }

        if (!formData.metaTitle.trim()) {
            newErrors.metaTitle = Language.GOOGLE_TITLE_REQUIRED;
            isValid = false;
        }

        if (!formData.metaKeyword.trim()) {
            newErrors.metaKeyword = Language.GOOGLE_KEYWORD_REQUIRED;
            isValid = false;
        } else if (
            !regexForCheckingCommaSeperatedString.test(
                formData.metaKeyword.trim(),
            )
        ) {
            newErrors.metaKeyword =
                Language.KEYWORD_SHOULD_CONTAIN_COMMA_SEPERATED;
            isValid = false;
        }

        if (!formData.metaDescription.trim()) {
            newErrors.metaDescription = Language.GOOGLE_DESCRIPTION_REQUIRED;
            isValid = false;
        }

        if (formData.youtubeLink != '') {
            if (!youtubeRegex.test(formData.youtubeLink.trim())) {
                newErrors.youtubeLink = Language.YOUTUBE_LINK_NOT_VALID;
                isValid = false;
            }
        }

        if (!formData.shortDescription.trim()) {
            newErrors.shortDescription = Language.SHORT_DESCRIPTION_REQUIRED;
            isValid = false;
        }

        if (!formData.description.trim()) {
            newErrors.description = Language.DESCRIPTION_REQUIRED;
            isValid = false;
        }

        setErrors(newErrors);

        const errorFields = Object.keys(newErrors);

        if (errorFields.length > 0) {
            const firstErrorElement = document.getElementsByName(
                errorFields[0],
            )[0];
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                firstErrorElement.focus();
            } else if (errorFields[0] === 'description') {
                const editorElement = document.getElementById('description');
                if (editorElement) {
                    editorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }

            return false;
        }

        return isValid;
    };

    const resetFieldsAndGoBack = () => {
        setFormData({
            articleTitle: '',
            url: '',
            metaTitle: '',
            metaKeyword: '',
            metaDescription: '',
            youtubeLink: '',
            documentText: '',
            shortDescription: '',
            description: '',
            thumbnail: null,
        });
        setTranslationData(null);
        navigate(-1);
    };

    const saveTranslation = async () => {
        setShowLoader(true);
        try {
            let payload: any = {
                ...formData,
                articleId: id,
                languageId: APP_LANGAUGE[language],
                folderName,
            };

            const { success, message } = await articleService.create(payload);
            if (success) {
                notification.success(message);
                resetFieldsAndGoBack();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const updateTranslation = async () => {
        setShowLoader(true);
        try {
            let payload = {
                ...formData,
                articleId: id,
                languageId: APP_LANGAUGE[language],
                articleTranslationId: translationData?.articleTranslationId,
                thumbnail: imageChanged ? formData.thumbnail : '',
            };
            const { success, message } = await articleService.update(payload);
            if (success) {
                notification.success(message);
                resetFieldsAndGoBack();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (isEdit) {
            updateTranslation();
        } else {
            saveTranslation();
        }
    };

    //to covert into camelcase by using same value from language file.
    const convertToCamelCase = (str: string): string => {
        return str.toLowerCase().replace(/ (.)/, (_, c) => c.toUpperCase());
    };

    const _checkForUrl = async () => {
        if (translationData?.url === formData.url || formData.url === '') {
            return true;
        } else {
            try {
                const { success, message } = await articleService.isUrlExists(
                    formData.url,
                    isFooterLink,
                );
                if (success) {
                    const newErrors: FormErrors = {};
                    newErrors.url = message;
                    setErrors(newErrors);
                }
            } catch (error) {
                notification.error(Language.SOMETHING_IS_WRONG);
                logger.error(error);
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
            'image/webp',
        ];
        const maxSizeInKB = 2048;

        const newErrors: FormErrors = {};

        if (file) {
            if (!allowedTypes.includes(file.type)) {
                newErrors.thumbnail = Language.INVALID_THUMBNAIL_FILE_TYPE;
                e.target.value = '';
                setErrors(newErrors);
                return;
            }

            const fileSizeInKB = file.size / 1024;
            if (fileSizeInKB > maxSizeInKB) {
                newErrors.thumbnail = Language.THUMBNAIL_FILE_LARGE;
                e.target.value = '';
                setErrors(newErrors);
                return;
            }

            setFormData((prev: any) => ({ ...prev, thumbnail: file }));
        }
    };

    return (
        <Layout>
            {!translationDataLoading && (
                <>
                    {showLoader && <Loader />}
                    <CCard className="mb-4">
                        <CCardHeader>
                            <strong>
                                {name} / {LANGAUGE_NAME[language]}
                            </strong>
                        </CCardHeader>
                        <CCardBody>
                            <CForm onSubmit={handleSubmit}>
                                <CFormLabel
                                    htmlFor={convertToCamelCase(
                                        Language.THUMBNAIL,
                                    )}
                                >
                                    {Language.ARTICLE_THUMBNAIL_IMAGE}
                                </CFormLabel>
                                <CFormInput
                                    type="file"
                                    name={convertToCamelCase(
                                        Language.THUMBNAIL,
                                    )}
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                    onChange={handleFileChange}
                                    invalid={!!errors.thumbnail}
                                    feedback={errors.thumbnail}
                                    ref={fileInputRef}
                                />
                                {formData.thumbnail && (
                                    <div className="mt-3">
                                        <div
                                            className="d-inline-block position-relative"
                                            style={{ maxWidth: '100%' }}
                                        >
                                            <img
                                                src={
                                                    isEdit
                                                        ? imageChanged
                                                            ? URL.createObjectURL(
                                                                  formData.thumbnail,
                                                              )
                                                            : formData.thumbnail
                                                        : URL.createObjectURL(
                                                              formData.thumbnail,
                                                          )
                                                }
                                                alt="Thumbnail Preview"
                                                style={{
                                                    maxHeight: '300px',
                                                    borderRadius: '4px',
                                                    display: 'block',
                                                }}
                                                className="img-fluid"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData(
                                                        (prev: any) => ({
                                                            ...prev,
                                                            thumbnail: null,
                                                        }),
                                                    );
                                                    if (fileInputRef?.current) {
                                                        fileInputRef.current.value =
                                                            '';
                                                    }
                                                    setImageChanged(true);
                                                }}
                                                className="btn btn-sm btn-dark position-absolute top-0 end-0 m-2 rounded-circle d-flex justify-content-center align-items-center"
                                                aria-label="Remove thumbnail"
                                                style={{
                                                    width: '30px',
                                                    height: '30px',
                                                }}
                                            >
                                                <CIcon icon={cilX} size="sm" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <CFormLabel
                                    className="mt-4"
                                    htmlFor={convertToCamelCase(
                                        Language.ARTICLE_TITLE,
                                    )}
                                >
                                    {Language.ARTICLE_TITLE +
                                        ' ' +
                                        Language.IN +
                                        ' ' +
                                        LANGAUGE_NAME[language]}
                                </CFormLabel>
                                <CFormInput
                                    value={formData.articleTitle}
                                    invalid={!!errors.articleTitle}
                                    feedback={errors.articleTitle}
                                    type="text"
                                    name={convertToCamelCase(
                                        Language.ARTICLE_TITLE,
                                    )}
                                    placeholder={
                                        Language.ENTER_ARTICLE_TITLE +
                                        ' ' +
                                        Language.IN.toLowerCase() +
                                        ' ' +
                                        LANGAUGE_NAME[language].toLowerCase()
                                    }
                                    onChange={handleChange}
                                    maxLength={400}
                                />
                                <LengthChecker
                                    currentLength={formData.articleTitle.length}
                                    maxLength={400}
                                />

                                <CFormLabel
                                    htmlFor={convertToCamelCase(Language.URL)}
                                >
                                    {Language.URL}
                                </CFormLabel>
                                <CFormInput
                                    type="text"
                                    placeholder={`Ex. ${name.toLowerCase().split(' ').join('-')}`}
                                    value={formData.url}
                                    invalid={!!errors.url}
                                    feedback={errors.url}
                                    onChange={handleChange}
                                    onBlur={_checkForUrl}
                                    name={convertToCamelCase(Language.URL)}
                                    maxLength={200}
                                />
                                <LengthChecker
                                    currentLength={formData.url.length}
                                    maxLength={200}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.GOOGLE_TITLE,
                                    )}
                                >
                                    {Language.GOOGLE_TITLE}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.ARTICLE_META_TITLE_HINT}
                                </HintText>
                                <CFormInput
                                    type="text"
                                    value={formData.metaTitle}
                                    placeholder={Language.ENTER_GOOGLE_TITLE}
                                    onChange={handleChange}
                                    invalid={!!errors.metaTitle}
                                    feedback={errors.metaTitle}
                                    name={convertToCamelCase(
                                        Language.META_TITLE,
                                    )}
                                    maxLength={300}
                                />
                                <LengthChecker
                                    currentLength={formData.metaTitle.length}
                                    maxLength={300}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.GOOGLE_KEYWORD,
                                    )}
                                >
                                    {Language.GOOGLE_KEYWORD}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.ARTICLE_META_KEYWORD_HINT}
                                </HintText>
                                <CFormInput
                                    type="text"
                                    placeholder={
                                        Language.META_KEYWORD_PLACEHOLDER
                                    }
                                    onChange={handleChange}
                                    value={formData.metaKeyword}
                                    invalid={!!errors.metaKeyword}
                                    feedback={errors.metaKeyword}
                                    name={convertToCamelCase(
                                        Language.META_KEYWORD,
                                    )}
                                    maxLength={300}
                                />
                                <LengthChecker
                                    currentLength={formData.metaKeyword.length}
                                    maxLength={300}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.GOOGLE_DESCRIPTION,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.GOOGLE_DESCRIPTION}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.ARTICLE_META_DESCRIPTION_HINT}
                                </HintText>
                                <CFormTextarea
                                    placeholder={
                                        Language.ENTER_GOOGLE_DESCRIPTION
                                    }
                                    onChange={handleChange}
                                    value={formData.metaDescription}
                                    invalid={!!errors.metaDescription}
                                    feedback={errors.metaDescription}
                                    style={{ height: 180 }}
                                    name={convertToCamelCase(
                                        Language.META_DESCRIPTION,
                                    )}
                                    maxLength={1000}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData.metaDescription.length
                                    }
                                    maxLength={1000}
                                />

                                <CFormLabel
                                    htmlFor={convertToCamelCase(
                                        Language.YOUTUBE_LINK,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.YOUTUBE_LINK}
                                </CFormLabel>
                                <CFormInput
                                    type="text"
                                    placeholder={
                                        Language.ENTER_YOUTUBE_VIDEO_LINK
                                    }
                                    onChange={handleChange}
                                    value={formData.youtubeLink}
                                    invalid={!!errors.youtubeLink}
                                    feedback={errors.youtubeLink}
                                    name={convertToCamelCase(
                                        Language.YOUTUBE_LINK,
                                    )}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.SHORT_DESCRIPTION,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.SHORT_DESCRIPTION}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.ARTICLE_SHORT_DESCRIPTION_HINT}
                                </HintText>
                                <CFormTextarea
                                    placeholder={
                                        Language.ENTER_SHORT_DESCRIPTION
                                    }
                                    onChange={handleChange}
                                    value={formData.shortDescription}
                                    invalid={!!errors.shortDescription}
                                    feedback={errors.shortDescription}
                                    maxLength={1000}
                                    style={{ height: 180 }}
                                    name={convertToCamelCase(
                                        Language.SHORT_DESCRIPTION,
                                    )}
                                />
                                <LengthChecker
                                    currentLength={
                                        formData.shortDescription.length
                                    }
                                    maxLength={1000}
                                />

                                <CFormLabel
                                    style={{ marginBottom: 0 }}
                                    htmlFor={convertToCamelCase(
                                        Language.DESCRIPTION,
                                    )}
                                    className="mt-3"
                                >
                                    {Language.DESCRIPTION}
                                </CFormLabel>
                                <HintText marginBottom="10px">
                                    {Language.ARTICLE_DESCRIPTION_HINT}
                                </HintText>
                                <div id="description">
                                    <JoditEditor
                                        ref={editor}
                                        value={formData.description}
                                        config={Editorconfig}
                                        onBlur={(newContent) =>
                                            setFormData({
                                                ...formData,
                                                description: newContent,
                                            })
                                        }
                                    />
                                </div>
                                {errors.description && (
                                    <div className="alert alert-danger">
                                        {Language.DESCRIPTION_REQUIRED}
                                    </div>
                                )}

                                <div className="d-flex justify-content-end">
                                    <CButton
                                        color="success"
                                        type="submit"
                                        className="mt-4"
                                    >
                                        {isEdit
                                            ? Language.UPDATE_ARTICLE
                                            : Language.SAVE_ARTICLE}
                                    </CButton>
                                </div>
                            </CForm>
                        </CCardBody>
                    </CCard>
                </>
            )}
        </Layout>
    );
};

export default ArticleTranslation;
